import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const Websites = () => {
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const websites = [
    {
      id: 1,
      title: 'BYR Construction UK',
      description: 'Professional construction company website with dynamic content, portfolio showcase, and client interaction features.',
      image: '/assets/byr1.png',
      technologies: ['React', 'Tailwind CSS', 'Backend API', 'CMS Integration'],
      liveUrl: 'https://www.byrconstruction.uk/',
      githubUrl: 'https://github.com/Mustan-dev',
      features: ['Portfolio Gallery', 'Service Showcase', 'Client Testimonials', 'Contact Form Integration'],
      fullDescription: 'BYR Construction UK is a professional, fully functional website designed for a construction company to present services, completed projects, and client feedback. It includes backend integration for email and inquiries, giving users an interactive and real-world experience while showcasing the company\'s expertise.',
      overview: 'This project demonstrates the creation of a professional, full-stack website that is visually appealing, fully responsive, and interactive. It highlights skills in React development, Tailwind CSS, backend API integration, responsive UI/UX design, and dynamic content management.',
      keyFeatures: [
        'Portfolio Gallery: High-quality project images with descriptions, dynamically loaded via backend.',
        'Service Showcase: Clear presentation of all company services with calls to action.',
        'Client Testimonials: Displays real feedback fetched dynamically from the backend.',
        'Contact Form with Email Integration: Visitors can submit inquiries directly to the company\'s email.',
        'Interactive UI & Responsive Design: Smooth animations and optimized layouts for all devices.'
      ],
      techDetails: [
        'React – Modern, component-based frontend for fast, maintainable development.',
        'Tailwind CSS – Clean, responsive, and utility-first styling.',
        'Responsive Design – Optimized for desktops, tablets, and mobile devices.',
        'Backend API Integration – Handles contact form submissions and dynamic content.',
        'CMS Integration – Easily manage services, projects, and testimonials without editing code.'
      ]
    },
    {
      id: 2,
      title: 'Av.Okan Göler',
      description: 'Comprehensive website for a legal professional, showcasing services, expertise, and client engagement features.',
      image: '/assets/avukat1.png',
      technologies: ['React', 'Tailwind CSS', 'Backend API', 'CMS Integration'],
      liveUrl: 'https://avokangoler.netlify.app/',
      githubUrl: 'https://github.com/Mustan-dev',
      features: ['Service Showcase', 'Case Studies', 'Client Testimonials', 'Consultation Requests'],
      fullDescription: 'Av.Okan Göler is a professional, fully functional website for a lawyer, designed to present legal services, highlight areas of expertise, and facilitate client communication. The site includes backend functionality for secure contact forms, consultation requests, and dynamic content management, ensuring it\'s not just visually polished but also fully interactive and practical for real-world use.',
      overview: 'This project demonstrates the creation of a professional, client-oriented website for a legal professional, combining modern design, backend integration, and dynamic content management. It highlights skills in React, Tailwind CSS, responsive UI/UX design, backend API integration, and full-stack development, making it an excellent showcase for real-world, professional web development.',
      keyFeatures: [
        'Service Showcase: Clear presentation of legal services with descriptions and call-to-action elements.',
        'Case Studies & Portfolio: Highlights successful cases in a professional, structured layout.',
        'Client Testimonials: Displays authentic client feedback dynamically via backend integration.',
        'Contact Form & Consultation Requests: Secure, fully functional form for consultation requests.',
        'Blog/Articles Section: Area for sharing legal insights, news, and updates.',
        'Interactive & Responsive UI: Smooth animations and optimized layouts for all devices.'
      ],
      techDetails: [
        'React – Modern, component-based frontend architecture for fast and maintainable development.',
        'Tailwind CSS – Clean, responsive styling for a professional, modern look.',
        'Responsive Design – Fully optimized for desktops, tablets, and mobile devices.',
        'Backend API Integration – Handles contact forms, client inquiries, and dynamic content.',
        'CMS Integration – Easy management of services, case studies, testimonials, and blog posts.'
      ]
    },
    {
      id: 3,
      title: 'Leisure Centre',
      description: 'Full-featured web application for managing leisure centre bookings, with user and admin functionality.',
      image: '/assets/leisurecenter.png',
      technologies: ['React', 'Tailwind CSS', 'Backend & Database', 'User Authentication'],
      liveUrl: 'https://leisurecentre.netlify.app/',
      githubUrl: 'https://github.com/Mustan-dev',
      features: ['User Authentication', 'Booking Management', 'Admin Dashboard', 'Role-Based Access'],
      fullDescription: 'The Leisure Centre Booking System is a comprehensive web application that allows users to register, log in, and manage bookings while providing admins with full control. Admins can view all users, bookings, and configure system settings.',
      overview: 'This project demonstrates complex full-stack development with database integration, user management, and admin functionality. It showcases skills in React, backend systems, responsive design, user authentication, and role-based access control.',
      keyFeatures: [
        'User Authentication: Secure login and registration for users and admins.',
        'Booking Management: Users can create, edit, or remove bookings easily.',
        'Admin Dashboard: Full control panel for viewing bookings and managing users.',
        'Role-Based Access: Admin features only visible to authorized accounts.',
        'Database Integration: Stores user accounts and bookings securely.'
      ],
      techDetails: [
        'React – Component-based frontend for scalable development.',
        'Tailwind CSS – Clean, responsive styling for modern design.',
        'Backend & Database Integration – Handles authentication and bookings.',
        'User Authentication – Secure login system with role-based access.',
        'Responsive Design – Optimized for desktop, tablet, and mobile.'
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Web Projects - Mustan-Dev | Full-Stack Web Development Portfolio</title>
        <meta 
          name="description" 
          content="Explore my collection of professional web projects and applications. From enterprise websites to interactive platforms, showcasing modern full-stack development with React, Node.js, and cloud technologies."
        />
        <meta name="keywords" content="Web Development, Full-Stack Projects, React, Node.js, Web Apps, Portfolio, Case Studies, Web Projects" />
        <meta property="og:title" content="Web Projects - Mustan-Dev" />
        <meta property="og:description" content="Professional web projects and applications built with modern technologies" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mustan.dev/web-projects" />
        <link rel="canonical" href="https://mustan.dev/web-projects" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <main className="pt-16">
          {/* Hero Section */}
          <section className="py-20 md:py-28 px-4 sm:px-6 lg:px-8 border-b border-border">
            <div className="max-w-6xl mx-auto">
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border bg-primary/10">
                  <Icon name="Globe" size={16} className="text-primary" />
                  <span className="text-sm font-medium">Web Projects</span>
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground">
                  Web Projects I've Built
                </h1>
                <p className="text-xl text-muted-foreground max-w-3xl">
                  A showcase of modern web applications and websites built with cutting-edge technologies. Each project demonstrates full-stack expertise, responsive design, and best practices in web development.
                </p>
                <div className="flex gap-4 pt-4">
                  <Button variant="primary">
                    <a href="https://github.com/Mustan-dev?tab=repositories" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                      <Icon name="Github" size={20} />
                      View on GitHub
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* Websites Grid */}
          <section className="py-20 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {websites.map((website) => (
                  <div 
                    key={website.id} 
                    className="group rounded-xl border border-border bg-card hover:bg-card/80 overflow-hidden transition-all duration-300 hover:border-primary/50 hover:shadow-lg"
                  >
                  <div className="relative h-48 overflow-hidden bg-muted">
                      <a 
                        href={website.liveUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full h-full"
                      >
                        <img 
                          src={website.image} 
                          alt={website.title}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 cursor-pointer"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300" />
                      </a>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-4">
                      <h3 className="text-lg font-semibold text-foreground line-clamp-1">
                        {website.title}
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {website.description}
                      </p>

                      {/* Technologies */}
                      <div className="flex flex-wrap gap-2">
                        {website.technologies.map((tech) => (
                          <span 
                            key={tech}
                            className="text-xs px-3 py-1 rounded-full bg-primary/10 text-primary border border-primary/20"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>

                      {/* Features */}
                      <div className="pt-2 border-t border-border">
                        <p className="text-xs font-semibold text-muted-foreground mb-2">Features</p>
                        <ul className="space-y-1">
                          {website.features.slice(0, 2).map((feature) => (
                            <li key={feature} className="text-xs text-muted-foreground flex items-start gap-2">
                              <Icon name="Check" size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Links */}
                      <div className="flex gap-2 pt-4">
                        <a 
                          href={website.liveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 px-3 py-2 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 text-xs font-semibold transition-colors text-center"
                        >
                          View Live
                        </a>
                        <button 
                          onClick={() => setSelectedProject(website)}
                          className="flex-1 px-3 py-2 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 text-foreground text-xs font-semibold transition-colors text-center"
                        >
                          Details
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Stats Section */}
          <section className="py-20 px-4 sm:px-6 lg:px-8 bg-card border-t border-border">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                <div className="text-center space-y-2">
                  <div className="text-3xl md:text-4xl font-bold text-primary">
                    4+
                  </div>
                  <p className="text-sm text-muted-foreground">Web Projects Built</p>
                </div>
                <div className="text-center space-y-2">
                  <div className="text-3xl md:text-4xl font-bold text-primary">
                    30+
                  </div>
                  <p className="text-sm text-muted-foreground">Happy Clients</p>
                </div>
                <div className="text-center space-y-2">
                  <div className="text-3xl md:text-4xl font-bold text-primary">
                    4+
                  </div>
                  <p className="text-sm text-muted-foreground">Years Experience</p>
                </div>
                <div className="text-center space-y-2">
                  <div className="text-3xl md:text-4xl font-bold text-primary">
                    99%
                  </div>
                  <p className="text-sm text-muted-foreground">Uptime</p>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto bg-gradient-to-r from-primary/20 to-primary/10 border border-primary/30 rounded-2xl p-12 text-center space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                Have a project in mind?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Start a conversation about your next web project. Tell me about your vision and let's explore how we can work together.
              </p>
              <a 
                href="/contact?type=web-project" 
                className="inline-flex items-center gap-2 px-8 py-3 rounded-lg bg-primary hover:bg-primary/90 text-white font-semibold transition-colors"
              >
                <Icon name="Mail" size={20} />
                Start a Conversation
              </a>
            </div>
          </section>

          {/* Project Details Modal */}
          {selectedProject && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-border p-6 flex items-center justify-between">
                  <h2 className="text-3xl font-bold text-slate-900">{selectedProject.title}</h2>
                  <button
                    onClick={() => setSelectedProject(null)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <Icon name="X" size={24} className="text-slate-600" />
                  </button>
                </div>

                <div className="p-6 space-y-8">
                  {/* Project Image */}
                  <img 
                    src={selectedProject.image} 
                    alt={selectedProject.title}
                    className="w-full h-80 object-cover rounded-xl"
                  />

                  {/* Description */}
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-3">Project Overview</h3>
                    <p className="text-slate-700 leading-relaxed">{selectedProject.fullDescription}</p>
                  </div>

                  {/* Key Features */}
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-4">Key Features</h3>
                    <ul className="space-y-3">
                      {selectedProject.keyFeatures.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <Icon name="Check" size={20} className="text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Technologies */}
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-4">Technologies Used</h3>
                    <ul className="space-y-3">
                      {selectedProject.techDetails.map((tech, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <Icon name="Code" size={20} className="text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-slate-700">{tech}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Summary */}
                  <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl p-6 border border-primary/20">
                    <h3 className="text-lg font-semibold text-slate-900 mb-3">Summary</h3>
                    <p className="text-slate-700 leading-relaxed">{selectedProject.overview}</p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-4 pt-4 border-t border-border">
                    <a
                      href={selectedProject.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 px-6 py-3 bg-primary hover:bg-primary/90 text-white rounded-lg font-semibold transition-colors text-center flex items-center justify-center gap-2"
                    >
                      <Icon name="ExternalLink" size={20} />
                      View Live Project
                    </a>
                    <button
                      onClick={() => setSelectedProject(null)}
                      className="flex-1 px-6 py-3 border border-border hover:bg-slate-50 rounded-lg font-semibold transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default Websites;
